'use client'

import { useCallback, useMemo, useState } from 'react'

import {
  CustomerAgeStatus,
  getCustomerAgeStatus,
  useCustomerData
} from 'modules/customer'
import { ValueOf } from 'shared/types/generic'
import toast from 'shared/ui/utils/toast'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import { ProductTypes } from '../types/products'
import { isProductBlockedByCustomerAge } from '../utils/products'

export const BlurProductMessages = {
  [CustomerAgeStatus.underage]:
    'Nie spełniasz wymagań wiekowych, aby zobaczyć ten produkt',
  [CustomerAgeStatus.unknown]: 'Potwierdź datę urodzenia',
  [CustomerAgeStatus.adult]: 'Produkt z ograniczeniem wiekowym',
  loggedOut: 'Zaloguj się, aby zobaczyć produkt'
} as const

export type BlurProductMessages = ValueOf<typeof BlurProductMessages>

interface UseProductAgeGateProps {
  product: Pick<ProductTypes, 'ageLimitMet' | 'ageLimit'>
  ageLimitMetCategory?: boolean | undefined
}

const useProductAgeGate = ({
  product,
  ageLimitMetCategory
}: UseProductAgeGateProps) => {
  const { user: customer } = useCustomerData()
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [dobModalOpen, setDobModalOpen] = useState(false)
  const isUserLoggedIn = isNotNilOrEmpty(customer)
  const userAgeStatus = useMemo(
    () => getCustomerAgeStatus(customer?.dateOfBirth),
    [customer?.dateOfBirth]
  )

  const isProductBlurred = useMemo(
    () =>
      isProductBlockedByCustomerAge({
        product,
        ageLimitMetCategory,
        userAgeStatus
      }),
    [product, ageLimitMetCategory, userAgeStatus]
  )

  const blurProductMessage = useMemo(
    () =>
      isUserLoggedIn
        ? BlurProductMessages[userAgeStatus]
        : BlurProductMessages.loggedOut,
    [isUserLoggedIn, userAgeStatus]
  )

  const handleBlurredProductClick = useCallback(
    (
      e:
        | React.MouseEvent<HTMLDivElement | HTMLButtonElement>
        | React.KeyboardEvent<HTMLDivElement | HTMLButtonElement>
    ) => {
      e.preventDefault()
      e.stopPropagation()

      if (!isUserLoggedIn) {
        setLoginModalOpen(true)

        return
      }

      if (userAgeStatus === CustomerAgeStatus.unknown) {
        setDobModalOpen(true)

        return
      }

      if (userAgeStatus === CustomerAgeStatus.underage) {
        toast.error('Produkt z ograniczeniem wiekowym')

        return
      }
    },
    [isUserLoggedIn, userAgeStatus]
  )

  const handleCloseLoginModal = useCallback(() => {
    setLoginModalOpen(false)
  }, [])

  const handleCloseDobModal = useCallback(() => {
    setDobModalOpen(false)
  }, [])

  return {
    userAgeStatus,
    loginModalOpen,
    dobModalOpen,
    isProductBlurred,
    blurProductMessage,
    handleBlurredProductClick,
    handleCloseLoginModal,
    handleCloseDobModal
  }
}

export default useProductAgeGate
